import { useEffect, useState } from 'react';
import { api, type RecentNotificationsResponse } from '../api/client';

/**
 * Is the alert pipeline actually delivering?
 *
 * Exists because of a five-day incident (2026-07): a site went unmonitored, the platform alerted
 * 405 times, and every attempt died on a misconfigured webhook — with nothing anywhere showing it.
 * The records were all in the database; no screen surfaced them. An alert channel whose failures
 * are invisible is indistinguishable from a working one, and this panel is the difference — placed
 * next to the destinations, where the fix is.
 *
 * Fetches on mount (the PushToggle lesson: never render a claim the component has not checked).
 */
export function DeliveryHealth() {
  const [data, setData] = useState<RecentNotificationsResponse | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    api
      .recentNotifications()
      .then(setData)
      .catch(() => setUnavailable(true));
  }, []);

  if (unavailable) {
    // Say we could not check, never that all is well — "unknown" and "healthy" are different claims.
    return <p className="muted">Delivery history could not be loaded.</p>;
  }
  if (!data) return null;

  const { sent, failed, suppressed, logged, lastFailure } = data.summary;
  const total = sent + failed + suppressed + logged;
  if (total === 0) {
    return <p className="muted">No alerts in the last 7 days.</p>;
  }

  const parts = [
    `${sent} delivered`,
    ...(failed > 0 ? [`${failed} failed`] : []),
    ...(suppressed > 0 ? [`${suppressed} suppressed as repeats`] : []),
    ...(logged > 0 ? [`${logged} recorded with nowhere to send`] : []),
  ];

  return (
    <div>
      <p className="muted">Last 7 days: {parts.join(' · ')}.</p>
      {failed > 0 && lastFailure && (
        <p className="warnbar">
          Alerts are failing to deliver
          {lastFailure.delivery ? (
            <>
              {' '}
              — last failure: <code>{lastFailure.delivery}</code>
            </>
          ) : null}{' '}
          ({new Date(lastFailure.at).toLocaleString()}). Check the destinations above.
        </p>
      )}
      {failed === 0 && logged > 0 && (
        <p className="warnbar">
          Some alerts had nowhere to go — add a destination above so they reach someone.
        </p>
      )}
    </div>
  );
}
